/**
 * Presses on a drawer/modal scrim must dismiss it and must not fall through
 * to the page once the scrim unmounts (the delayed "ghost click").
 *
 * Installed once. Existing scrims keep their own onClick close handlers;
 * this cancels the pointer and replays a click on the scrim only.
 */

function backgroundAlpha(color: string): number {
    const match = color.match(/rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*(?:,\s*([\d.]+)\s*)?\)/);
    if (!match) return color === "transparent" ? 0 : 1;
    if (match[1] == null) return 1;
    const alpha = Number(match[1]);
    return Number.isFinite(alpha) ? alpha : 1;
}

/** True when this node is the dimmed (or full-screen empty) layer behind a panel. */
export function isOverlayDismissScrim(el: Element): boolean {
    if (el.getAttribute("data-c8-overlay-dismiss") === "true") return true;
    if (el.classList.contains("ReactModal__Overlay")) return true;
    if (el.getAttribute("role") === "dialog" || el.getAttribute("aria-modal") === "true") return false;

    const style = window.getComputedStyle(el);
    if (style.pointerEvents === "none" || style.display === "none" || style.visibility === "hidden") return false;
    if (style.position !== "fixed" && style.position !== "absolute") return false;

    const rect = el.getBoundingClientRect();
    if (rect.width < window.innerWidth * 0.85) return false;
    if (rect.height < window.innerHeight * 0.35) return false;

    const alpha = backgroundAlpha(style.backgroundColor);
    const translucentScrim = alpha > 0.04 && alpha < 0.96;
    const emptyCatcher = el.childElementCount === 0 && !(el.textContent || "").trim();
    const z = Number.parseInt(style.zIndex, 10);
    const elevated = Number.isFinite(z) && z >= 1000;
    if (translucentScrim && (elevated || el.childElementCount === 0)) return true;
    if (emptyCatcher && elevated) return true;
    return false;
}

export function installOverlayDismissGuard(): void {
    if (typeof document === "undefined") return;
    const doc = document as Document & { __c8OverlayDismissGuard?: boolean };
    if (doc.__c8OverlayDismissGuard) return;
    doc.__c8OverlayDismissGuard = true;

    document.addEventListener(
        "pointerdown",
        (event) => {
            if (event.button !== 0) return;
            const target = event.target;
            if (!(target instanceof Element)) return;
            if (!isOverlayDismissScrim(target)) return;
            event.preventDefault();
            event.stopPropagation();
            if (target instanceof HTMLElement) target.click();
        },
        true
    );
}
